import React from 'react';
import { motion } from 'framer-motion';
import { TECH_NODES, TECH_BRANCHES } from '../../game/data/technology';
import { ColorProgressBar } from '../ui/ProgressBar';

const BRANCH_ORDER = ['A', 'B', 'C', 'D'];

export function TechTreeScreen({ state, dispatch }) {
  const labRoom = state.rooms?.find(r => r.id === 'room-lab');
  const scientistRP = state.workers
    .filter(w => w.type === 'scientist' && w.assignedRoomId === 'lab')
    .reduce((s, w) => s + Math.round((10 + (w.skill / 100) * 15) * (w.morale / 100)), 0);
  const labEquip = labRoom?.equipment || {};
  const equipBonus = scientistRP > 0 ? Object.values(labEquip).reduce((s, e) => s + (e?.installed ? (e.rpBonus || 0) : 0), 0) : 0;
  const weeklyRP = scientistRP + equipBonus;

  const nodesByBranch = BRANCH_ORDER.reduce((acc, branch) => {
    acc[branch] = Object.values(TECH_NODES).filter(n => n.branch === branch).sort((a, b) => a.tier - b.tier);
    return acc;
  }, {});

  function isNodeAvailable(node) {
    if (node.minStage > state.stage) return false;
    if (node.requires.length === 0) return true;
    return node.requires.every(req => state.unlockedTech.includes(req));
  }

  return (
    <div className="screen">

      {/* Research progress bar */}
      {state.activeResearch && (() => {
        const node = TECH_NODES[state.activeResearch.nodeId];
        const progress = state.activeResearch.pointsAccumulated;
        const required = state.activeResearch.pointsRequired;
        return (
          <div className="research-progress-bar" style={{ borderColor: 'var(--blue)', marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div>
                <span className="rp-title">{node?.name}</span>
                <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>In Progress</span>
              </div>
              <span style={{ fontSize: 12, color: 'var(--blue)' }}>{progress} / {required} RP</span>
            </div>
            <ColorProgressBar value={progress} max={required} colorClass="fill-blue" size="lg" />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              {weeklyRP > 0
                ? `+${weeklyRP} RP/week from scientists · ~${Math.ceil((required - progress) / Math.max(1, weeklyRP))} weeks remaining`
                : 'Assign scientists to the lab to generate RP.'}
            </div>
          </div>
        );
      })()}

      {/* RP info row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <div className="panel" style={{ flex: 1, padding: '10px 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Research Points</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--blue)' }}>{state.researchPoints} RP</div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-muted)' }}>
              <div>+{weeklyRP} RP/week</div>
              <div>{state.workers.filter(w => w.type === 'scientist').length} scientists</div>
            </div>
          </div>
        </div>
        {weeklyRP === 0 && (
          <div className="panel" style={{ flex: 2, padding: '10px 14px', borderColor: 'var(--yellow-dim)' }}>
            <div style={{ fontSize: 11, color: 'var(--yellow)' }}>
              💡 Hire a Scientist and assign them to the "lab" to generate research points each week.
            </div>
          </div>
        )}
      </div>

      {/* Tech tree grid */}
      <div className="tech-tree">
        {BRANCH_ORDER.map(branchId => {
          const branch = TECH_BRANCHES[branchId];
          const nodes = nodesByBranch[branchId];
          return (
            <div key={branchId} className="tech-branch-col">
              <div className="tech-branch-header" style={{ background: branch.color + '22', color: branch.color, border: `1px solid ${branch.color}44` }}>
                {branch.icon} {branch.name}
              </div>

              {nodes.map((node, i) => {
                const isUnlocked   = state.unlockedTech.includes(node.id);
                const isActive     = state.activeResearch?.nodeId === node.id;
                const isAvailable  = isNodeAvailable(node);
                const isLocked     = !isAvailable;
                const canAfford    = state.cash >= node.cost;

                const nodeClass = [
                  'tech-node',
                  isUnlocked ? 'unlocked' : '',
                  isActive   ? 'active-research' : '',
                  isLocked   ? 'locked-tech' : '',
                ].filter(Boolean).join(' ');

                return (
                  <React.Fragment key={node.id}>
                    {i > 0 && (
                      <div className={`tech-branch-connector ${state.unlockedTech.includes(nodes[i-1].id) ? 'completed' : ''}`} />
                    )}
                    <motion.div
                      className={nodeClass}
                      whileHover={isLocked ? {} : { scale: 1.02, y: -2 }}
                      whileTap={isLocked ? {} : { scale: 0.98 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    >
                      {isUnlocked && <span className="tech-unlock-check">✓</span>}

                      <div className="tech-node-name" style={{ color: isUnlocked ? 'var(--green)' : 'var(--text)' }}>
                        {node.name}
                      </div>
                      <div className="tech-node-desc">{node.description}</div>

                      <div className="tech-node-cost">
                        <span className="cost-cash">💰 ${node.cost.toLocaleString()}</span>
                        <span className="cost-rp">🔬 {node.rp} RP</span>
                      </div>

                      {node.minStage > state.stage && (
                        <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>
                          Requires Stage {node.minStage}
                        </div>
                      )}

                      {!isUnlocked && !isActive && isAvailable && (
                        <button
                          className="btn btn-primary btn-sm"
                          style={{ marginTop: 8, width: '100%' }}
                          disabled={!canAfford || !!state.activeResearch}
                          onClick={() => dispatch({ type: 'START_RESEARCH', payload: { nodeId: node.id } })}
                          title={!canAfford ? `Need $${node.cost}` : state.activeResearch ? 'Research already in progress' : ''}
                        >
                          {state.activeResearch ? 'Research Busy' : `Research ($${node.cost.toLocaleString()})`}
                        </button>
                      )}

                      {isActive && (
                        <div style={{ marginTop: 6 }}>
                          <ColorProgressBar
                            value={state.activeResearch.pointsAccumulated}
                            max={node.rp}
                            colorClass="fill-blue"
                            size="sm"
                          />
                          <div style={{ fontSize: 10, color: 'var(--blue)', marginTop: 2 }}>
                            {state.activeResearch.pointsAccumulated}/{node.rp} RP
                          </div>
                        </div>
                      )}

                      {node.requires.length > 0 && !isLocked && (
                        <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>
                          Requires: {node.requires.map(r => TECH_NODES[r]?.name).join(', ')}
                        </div>
                      )}
                    </motion.div>
                  </React.Fragment>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
